import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook to track page visibility state using the Page Visibility API.
 *
 * Returns:
 * - `isVisible`: Whether the tab/page is currently visible
 * - `onBecomeVisible`: Register a callback to run when tab becomes visible
 *
 * @example
 * ```tsx
 * const { isVisible, onBecomeVisible } = usePageVisibility();
 *
 * // Defer expensive operations for background tabs
 * useEffect(() => {
 *   if (isVisible) {
 *     refetchData();
 *   } else {
 *     setPendingRefetch(true);
 *   }
 * }, [someEvent, isVisible]);
 *
 * // Run callback when tab becomes visible
 * onBecomeVisible(() => {
 *   if (pendingRefetch) {
 *     refetchData();
 *     setPendingRefetch(false);
 *   }
 * });
 * ```
 */
export function usePageVisibility() {
  const [isVisible, setIsVisible] = useState(() =>
    typeof document !== 'undefined' ? document.visibilityState === 'visible' : true,
  );

  // Store callbacks in a ref to avoid re-subscribing on every render
  const callbacksRef = useRef<Set<() => void>>(new Set());

  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = document.visibilityState === 'visible';
      setIsVisible(visible);

      // Fire all registered callbacks when becoming visible
      if (visible) {
        callbacksRef.current.forEach((callback) => callback());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  /**
   * Register a callback to run when the page becomes visible.
   * The callback is automatically cleaned up when the component unmounts.
   */
  const onBecomeVisible = useCallback((callback: () => void) => {
    callbacksRef.current.add(callback);
    return () => {
      callbacksRef.current.delete(callback);
    };
  }, []);

  return { isVisible, onBecomeVisible };
}
